import { ProductResult } from '@shared/api/types';
import { Icons } from '@shared/components/content/icons';
import { renderUnit } from '@shared/components/content/unit';
import { EmissionBarCell } from '@shared/components/data-table/cells/emission-bar-cell';
import { cn, formatSmallNumber } from '@shared/lib/utils';
import { PptSection } from './build-pdf-breakdown-pages';

const GRID_COLS = 'grid-cols-[minmax(0,1fr)_96px_150px]';

/** Mirrors the platform's processing, packaging and transport tables. */
export function buildPptSections(productResult: ProductResult): PptSection[] {
  const total = productResult.totalEmissions;
  if (!total) return [];
  const sections: PptSection[] = [];

  const processingTypes = productResult.processing.processingTypes ?? [];
  if (processingTypes.length) {
    sections.push({
      stage: 'processing',
      title: 'Processing',
      rowLabel: 'Type',
      shareOfTotal: productResult.processing.processingEmissions / total,
      rows: processingTypes.map((t) => ({
        name: t.name,
        emissions: t.totalEmissionsPerItem,
      })),
    });
  }
  const packagingTypes = productResult.packaging.packagingTypes ?? [];
  if (packagingTypes.length) {
    sections.push({
      stage: 'packaging',
      title: 'Packaging',
      rowLabel: 'Item',
      shareOfTotal: productResult.packaging.packagingEmissions / total,
      rows: packagingTypes.map((t) => ({
        name: t.name,
        emissions: t.totalEmissionsPerItem,
      })),
    });
  }
  const transportTypes = productResult.transport.transportTypes ?? [];
  if (transportTypes.length) {
    sections.push({
      stage: 'transport',
      title: 'Transport',
      rowLabel: 'Step',
      shareOfTotal: productResult.transport.transportEmissions / total,
      rows: transportTypes.map((t) => ({
        name: t.name,
        emissions: parseFloat(t.totalEmissionsPerItem),
      })),
    });
  }

  return sections.map((section) => ({
    ...section,
    rows: [...section.rows].sort((a, b) => b.emissions - a.emissions),
  }));
}

export function PptSectionTable({
  section,
  maxEmissions,
}: {
  section: PptSection;
  maxEmissions: number;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-end justify-between">
        <p className="font-heading text-sm font-bold text-primary-800">
          {section.title}
        </p>
        <span className="flex items-center gap-1 text-xs font-medium text-teal-600">
          <Icons.pieChart size={14} />
          {(section.shareOfTotal * 100).toFixed(1)}% of total emissions
        </span>
      </div>
      <div className="overflow-hidden rounded-md border border-gray-200 text-xs">
        <div
          className={cn(
            'grid items-end border-b border-gray-200 bg-gray-50 px-3 py-2 font-semibold text-gray-700',
            GRID_COLS
          )}
        >
          <span>{section.rowLabel}</span>
          <span className="pr-4 text-right">
            Emissions
            <span className="block text-[10px] font-normal leading-3 text-gray-500">
              {renderUnit('kgCO2e')}
            </span>
          </span>
          <span />
        </div>
        {section.rows.map((row, index) => (
          <div
            key={`${row.name}-${index}`}
            className={cn(
              'grid h-7 items-center border-b border-gray-100 px-3 text-gray-900 last:border-b-0',
              GRID_COLS
            )}
          >
            <span className="truncate pr-3">{row.name}</span>
            <span className="pr-4 text-right">
              {formatSmallNumber(row.emissions)}
            </span>
            <div className="h-full">
              <EmissionBarCell
                type={section.stage}
                emissions={row.emissions}
                maxEmissions={maxEmissions}
                showHover={false}
                className="w-full pr-2"
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
